import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Модель узлов ПКМ-меню (пак context-menu, F1 · SOLUTION §6).
 *
 * Меню НЕ описывается разметкой на поверхности — оно рендерится из массива
 * узлов, который отдаёт реестр действий (F2 для задач, F3 для проектов).
 * Это и есть инвариант I2: новое действие — запись в реестре, а не пункт
 * в компоненте.
 */

/** Ширина меню — по типу поверхности (макеты 66:2 / 68:62 / 67:53 / 68:41). */
export type ContextMenuWidth = 'task' | 'project' | 'sub' | 'blank';

/** Причина, по которой пункт видим, но недоступен (SOLUTION §5). */
export type DisabledInfo = {
  /** Локализованный текст причины — уходит в tooltip. */
  reason: string;
  /** Короткая пометка справа в пункте (в макете 68:114 — «admin»). */
  note?: string;
};

export type MenuItemNode = {
  kind: 'item';
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Хоткей-акселератор справа (моно, 11px). Источник — реестр, не компонент (I8). */
  hotkey?: string;
  /**
   * Значение пресета справа от подписи (макет `76:33`: «Сегодня — 10 мар»).
   * Без него пресет пришлось бы применять вслепую, чтобы узнать дату.
   */
  meta?: string;
  /** Danger-грейд: последняя группа меню (I4). */
  danger?: boolean;
  disabled?: DisabledInfo;
  /** Текущее значение в подменю-выборе — галочка справа (макет 67:61). */
  checked?: boolean;
  /**
   * Цвет точки-маркера вместо иконки (статусы/приоритеты/метки).
   * Значение — готовая CSS-строка из токена состояния (`var(--column-color-…)`).
   */
  dotColor?: string;
  /**
   * Цвет самой иконки, когда она несёт значение, а не категорию: флаг
   * приоритета в макете `76:15` окрашен по значению. Обычные пункты цвет не
   * задают и красятся `--ctxmenu-icon`.
   */
  iconColor?: string;
  /**
   * Семантика выбора для AT: `radio` — один из набора (статус, приоритет),
   * `checkbox` — независимый переключатель (метки). Задаёт роль пункта и
   * `aria-checked`; без неё галочка видна только глазами.
   */
  selection?: 'radio' | 'checkbox';
  /** Меню закрывается после выбора. Для чекбокс-пунктов (метки) — false. */
  closeOnSelect?: boolean;
  onSelect: () => void;
};

/**
 * Состояние строки поиска подменю. Живёт в самой панели: список исполнителей
 * фильтруется там же, где набирают, — иначе фильтр пришлось бы тянуть через
 * родителя и клавиатурная навигация по отфильтрованному списку разъехалась бы
 * с содержимым.
 */
export type MenuFilterCtl = {
  filter: string;
  setFilter: (value: string) => void;
  /** Пересобрать пункты панели — для переключателей, меняющих состояние вне React. */
  refresh: () => void;
  /** Номер пересборки: делает зависимость пунктов от `refresh` явной. */
  revision: number;
};

export type MenuSubNode = {
  kind: 'sub';
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Текущее значение поля справа от подписи — чтобы не раскрывать ради справки. */
  meta?: string;
  disabled?: DisabledInfo;
  width?: ContextMenuWidth;
  /**
   * Содержимое подменю. Функция, а не массив: списки статусов/исполнителей
   * тянутся из данных и не должны считаться, пока подменю закрыто.
   * Получает состояние поиска своей панели (F6 — порог >7 участников).
   */
  items: (ctl: MenuFilterCtl) => ContextMenuNode[];
};

export type MenuSeparatorNode = { kind: 'separator'; id: string };

/** CAPS-лейбл группы внутри подменю (макет 67:54 — StateGroup). */
export type MenuGroupLabelNode = {
  kind: 'group-label';
  id: string;
  label: string;
  /**
   * D5.5 (CM-F2) — свёртываемая группа. Выключенные пункты архивной задачи
   * перечислялись плоско под подписью-причиной: одиннадцать строк, ни по одной
   * из которых нельзя кликнуть, отодвигали доступные действия вниз. Подпись
   * становится переключателем; группа кончается следующей подписью, шапкой или
   * разделителем.
   */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Сколько пунктов скрыто — в свёрнутом виде это единственный след группы. */
  count?: number;
};

/** Шапка меню: имя+код проекта (68:63) или «Выбрано: N задач» (68:3). */
export type MenuHeaderNode = {
  kind: 'header';
  id: string;
  title: string;
  /** Моноширинный хвост: код проекта / перечень проектов выделения. */
  hint?: string;
};

/** Произвольный контент внутри меню — строка поиска (F6), DatePicker (F6). */
export type MenuCustomNode = {
  kind: 'custom';
  id: string;
  render: (ctl: { close: () => void } & MenuFilterCtl) => ReactNode;
};

export type ContextMenuNode =
  | MenuItemNode
  | MenuSubNode
  | MenuSeparatorNode
  | MenuGroupLabelNode
  | MenuHeaderNode
  | MenuCustomNode;

/** Узлы, по которым ходит клавиатура и которые принимают выбор. */
export type FocusableNode = MenuItemNode | MenuSubNode | MenuGroupLabelNode;

export function isFocusable(node: ContextMenuNode): node is FocusableNode {
  // Свёртываемая подпись — единственный узел не-`item`, принимающий выбор:
  // иначе свернуть группу можно было бы только мышью, а roving tabindex
  // перепрыгивал бы через переключатель, который видно глазами.
  if (node.kind === 'group-label') return node.collapsible === true;
  return node.kind === 'item' || node.kind === 'sub';
}

/**
 * Узлы под свёрнутыми подписями. Группа кончается следующей подписью, шапкой
 * или разделителем — то есть ровно там, где кончается её объяснение.
 *
 * Фильтрация делается ОДИН раз и до всех индексных расчётов: модель узлов
 * плоская и адресуется позицией, поэтому список, по которому ходит клавиатура,
 * обязан совпадать со списком, который отрисован. Иначе стрелки попадали бы
 * на скрытые пункты.
 */
export function visibleNodes(
  nodes: readonly ContextMenuNode[],
  collapsed: ReadonlySet<string>,
): ContextMenuNode[] {
  const out: ContextMenuNode[] = [];
  let hidingUnder: string | null = null;
  for (const node of nodes) {
    if (node.kind === 'group-label' || node.kind === 'header' || node.kind === 'separator') {
      hidingUnder =
        node.kind === 'group-label' && node.collapsible === true && collapsed.has(node.id)
          ? node.id
          : null;
      out.push(node);
      continue;
    }
    if (hidingUnder === null) out.push(node);
  }
  return out;
}

/** Полезная нагрузка одного открытия меню. */
export type ContextMenuPayload = {
  /** Координаты клика (viewport). */
  x: number;
  y: number;
  nodes: ContextMenuNode[];
  width: ContextMenuWidth;
  /** Элемент-источник — на него возвращается фокус по Esc. */
  source: HTMLElement | null;
  /** Для aria-label меню: «Действия над задачей» / «…над проектом». */
  ariaLabel: string;
};
